import type React from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon } from '@phosphor-icons/react';

// Floor so a near-empty crawl (no fans yet) still reads as a roll rather than a blink.
const MIN_DURATION_S = 6;

interface Fan {
  commenterName: string;
  count: number;
}

// Representative frozen frame for the editor's static preview: title at top, top fans
// filling the frame. Same expression as CreditsWorkbook's frozen snapshot (≈ -6%).
const FROZEN_TRANSLATE_Y = 100 + (-180 - 100) * 0.35;

interface CrawlStageProps {
  title: string;
  episodeLabel: string;
  topLabel: string;
  topFans: Fan[];
  restFans: Fan[];
  showCount: boolean;
  closingLine: string;
  speed: number;
  textColor: string;
  headingColor: string;
  fontFamily: string;
  headingFontFamily?: string;
  fontSizeScale?: number;
  showReplay?: boolean;
}

export default function CrawlStage({
  title,
  episodeLabel,
  topLabel,
  topFans,
  restFans,
  showCount,
  closingLine,
  speed,
  textColor,
  headingColor,
  fontFamily,
  headingFontFamily,
  fontSizeScale = 1,
  showReplay = false,
}: CrawlStageProps) {
  const sc = (px: number) => `${Math.round(px * fontSizeScale)}px`;
  const vw = (v: number) => `${(v * fontSizeScale).toFixed(2)}vw`;
  const [animationKey, setAnimationKey] = useState(0);

  const stageRef = useRef<HTMLDivElement>(null);
  const crawlRef = useRef<HTMLDivElement>(null);
  const [measuredDuration, setMeasuredDuration] = useState<number | null>(null);

  // The crawl travels 280% of its *own* height (translateY 100% -> -180%), and that height
  // grows with every name in the list. Running it over a fixed number of seconds therefore
  // makes long lists scroll faster — the opposite of what a credits roll should do. So drive
  // the duration off the measured content instead, which holds the velocity constant and just
  // makes a longer list take longer. `speed` is read as seconds per stage-height of content,
  // so a roll that's about one screen tall still takes ~`speed` seconds as it always has.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const crawl = crawlRef.current;
    if (!stage || !crawl) return undefined;

    let locked = false;
    let cancelled = false;

    const measure = () => {
      if (locked) return;
      const stageHeight = stage.clientHeight;
      const contentHeight = crawl.offsetHeight;
      if (!stageHeight || !contentHeight) return;
      const next = Math.max(MIN_DURATION_S, speed * (contentHeight / stageHeight));
      setMeasuredDuration(next);
    };

    measure();

    const fontsReady: Promise<unknown> = (document as any).fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      if (cancelled) return;
      measure();
      locked = true;
    });

    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    observer.observe(crawl);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [speed, animationKey]);

  const duration = measuredDuration ?? speed;

  const [playing, setPlaying] = useState(false);
  const isEditor = showReplay;
  const play = useCallback(() => { setAnimationKey(k => k + 1); setPlaying(true); }, []);
  const pause = useCallback(() => setPlaying(false), []);

  const crawlStyle: React.CSSProperties =
    isEditor && !playing
      ? { transform: `translateY(${FROZEN_TRANSLATE_Y}%)` }
      : { animation: `credits-crawl ${duration}s linear ${isEditor ? 'infinite' : 'both'}` };

  const maskFade = 'linear-gradient(to top, rgba(0,0,0,0) 0%, #000 30%, #000 62%, rgba(0,0,0,0) 74%)';
  const showFade = !(isEditor && !playing);

  const closingPaused = isEditor && !playing;
  const closingStyle: React.CSSProperties = closingPaused
    ? {}
    : { animation: `credits-closing ${duration}s linear ${isEditor ? 'infinite' : 'both'}` };

  return (
    <div
      ref={stageRef}
      className="relative w-full h-full overflow-hidden select-none"
      style={{
        fontFamily,
        // Steeper rotateX (60deg) shrinks the text faster, so the fade can hide less.
        ...(showFade ? { maskImage: maskFade, WebkitMaskImage: maskFade } : {}),
      }}
    >
      <style>{`
        @keyframes credits-crawl {
          0%   { transform: translateY(100%); opacity: 0; }
          6%   { opacity: 1; }
          82%  { opacity: 1; }
          100% { transform: translateY(-180%); opacity: 0; }
        }
        @keyframes credits-closing {
          0%, 60% { opacity: 0; transform: translateY(30%); }
          76%     { opacity: 1; transform: translateY(0); }
          100%    { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* 3-D perspective viewport */}
      <div
        className="absolute inset-0"
        style={{ perspective: '80vh' }}
      >
        <div
          className="absolute left-1/2 bottom-0 w-full"
          style={{
            // maxWidth: 1040,
            transform: 'translateX(-50%) rotateX(60deg)',
            transformOrigin: '50% 100%',
          }}
        >
          <div
            key={animationKey}
            ref={crawlRef}
            className="absolute bottom-0 left-0 right-0 px-[5%]"
            style={crawlStyle}
          >
            {title && (
              <div className="w-full flex justify-center" style={{ marginBottom: sc(36), paddingTop: sc(16) }}>
                <p
                  className="uppercase font-bold leading-[.88] tracking-[-0.02em] text-center"
                  style={{ color: headingColor, fontSize: `clamp(${sc(64)}, ${vw(14)}, ${sc(200)})`, fontFamily: headingFontFamily || fontFamily }}
                >
                  {title}
                </p>
              </div>
            )}

            {episodeLabel && (
              <p
                className="text-center uppercase tracking-[0.28em]"
                style={{ color: textColor, fontSize: `clamp(${sc(24)}, ${vw(2.8)}, ${sc(40)})`, lineHeight: 1.5, opacity: 0.95, marginBottom: sc(28) }}
              >
                {episodeLabel}
              </p>
            )}

            {topFans.length > 0 && (
              <>
                <p
                  className="text-center uppercase tracking-[0.22em]"
                  style={{ color: textColor, fontSize: `clamp(${sc(28)}, ${vw(3.2)}, ${sc(48)})`, lineHeight: 1.5, opacity: 0.85, marginBottom: sc(20), marginTop: sc(40) }}
                >
                  {topLabel}
                </p>
                <ul className="list-none text-center" style={{ marginBottom: sc(24) }}>
                  {topFans.map((fan, i) => {
                    const rank = Math.max(0.6, 1 - i * 0.1);
                    const namePx = Math.round(72 * rank * fontSizeScale);
                    const countPx = Math.round(48 * rank * fontSizeScale);
                    return (
                      <li
                        key={i}
                        className="font-semibold leading-[1.6] tracking-[0.04em]"
                        style={{ color: headingColor, fontSize: `clamp(${sc(32)}, ${(6 * rank * fontSizeScale).toFixed(2)}vw, ${namePx}px)` }}
                      >
                        {fan.commenterName}
                        {showCount && (
                          <span className="font-normal opacity-70" style={{ marginLeft: sc(52), fontSize: `clamp(${sc(22)}, ${(4 * rank * fontSizeScale).toFixed(2)}vw, ${countPx}px)` }}>
                            {fan.count}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {restFans.length > 0 && (
              <>
                <p
                  className="text-center uppercase tracking-[0.22em]"
                  style={{ color: textColor, fontSize: `clamp(${sc(24)}, ${vw(2.8)}, ${sc(40)})`, lineHeight: 1.5, opacity: 0.85, marginBottom: sc(20), marginTop: sc(40) }}
                >
                  Thank You
                </p>
                <ul className="list-none text-center" style={{ marginBottom: sc(32) }}>
                  {restFans.map((fan, i) => (
                    <li
                      key={i}
                      className="leading-[1.6]"
                      style={{ color: textColor, fontSize: `clamp(${sc(28)}, ${vw(3.8)}, ${sc(56)})` }}
                    >
                      {fan.commenterName}
                      {showCount && (
                        <span className="opacity-60" style={{ marginLeft: sc(44), fontSize: '0.7em' }}>
                          {fan.count}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div style={{ height: '30vh' }} />
          </div>
        </div>
      </div>

      {/* Closing line — rises into the middle and holds after the crawl (bigger/wider
          than the crawl text). Flat overlay, not part of the 3D crawl plane. */}
      {closingLine && (
        <div
          key={`closing-${animationKey}`}
          className={`absolute px-[8%] flex justify-center pointer-events-none z-[5] ${closingPaused
            ? 'inset-x-0 bottom-0 items-end pb-[5%]' // paused: sit at the bottom, below the fans
            : 'inset-0 items-center' // playing/live: rise into the middle
          }`}
          style={closingStyle}
        >
          <p
            className="text-center uppercase font-semibold tracking-[0.16em]"
            style={{
              color: textColor,
              // Smaller when paused so it doesn't collide with the frozen fan list; big
              // and centred while playing so it reads as the final card.
              fontSize: closingPaused
                ? `clamp(${sc(18)}, ${vw(2.6)}, ${sc(34)})`
                : `clamp(${sc(32)}, ${vw(5)}, ${sc(72)})`,
              lineHeight: 1.25,
              maxWidth: '90%',
            }}
          >
            {closingLine}
          </p>
        </div>
      )}

      {/* Play/Pause toggle — editor only. Paused shows the static frozen frame; Play
          runs the looping crawl from the top so the preview never holds a blank frame. */}
      {showReplay && <button
        type="button"
        onClick={playing ? pause : play}
        title={playing ? 'Pause' : 'Play'}
        className="absolute bottom-3 right-3 z-20 w-9 h-9 rounded-full grid place-items-center bg-primary-500 hover:bg-primary-600 text-white cursor-pointer transition-colors shadow-md"
      >
        {playing
          ? <PauseIcon size={16} weight="fill" />
          : <PlayIcon size={16} weight="fill" />}
      </button>}
    </div>
  );
}
